import { Partners, Users } from '@platform/client';
import { OrganizationApplicationParameters } from '@platform/client/schemas';
import { ErrorBox, InlineFormInput, MultiSelectDropdown } from '@platform/components';
import { useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import { OrgPartnerAssociationShowcase } from './OrgPartnerAssociationShowcase';

type FormValues = {
  impactPartners?: string[];
  otherPartners?: string;
  noPartners?: boolean;
};

/**
 * Hardcoded priority partners to display at the top of the dropdown.
 */
const PRIORITY_PARTNERS = ['ChangemakerXchange', 'Health Innovation Exchange'];

export const OrgPartnerAssociationSelection = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress, dataKey } = { ...props };

  const [showError, setShowError] = useState(false);

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<OrganizationApplicationParameters>(dataKey);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      impactPartners: getApplicationDataByKey('impactPartners') || [],
      otherPartners: getApplicationDataByKey('otherPartners') || '',
      noPartners:
        getApplicationDataByKey('impactPartners') &&
        getApplicationDataByKey('impactPartners').length === 0 &&
        !getApplicationDataByKey('otherPartners'),
    },
  });

  const partners = Partners.useGetActiveImpactPartners().data || [];
  const partnerOptions = partners.map((partner) => ({
    label: partner.name,
    textValue: partner.id,
  }));
  const prioritizedPartnerOptions = useMemo(() => {
    const orderedOptions = [
      ...partnerOptions.filter((p) => p.label && PRIORITY_PARTNERS.includes(p.label)),
      ...partnerOptions.filter((p) => p.label && !PRIORITY_PARTNERS.includes(p.label)),
    ];
    return orderedOptions;
  }, [partnerOptions]);

  // On first load, set the partner from the database signup affiliation
  // Honor final 'state' of dropdown after
  const userSignupPartner = Users.useGetUserSignupPartner().data;
  useEffect(() => {
    const partnerInitialized = localStorage.getItem('orgPartnerInitialized');
    const selectedPartner = prioritizedPartnerOptions.find(
      (partner) => partner.textValue === userSignupPartner?.id,
    );
    if (!partnerInitialized && selectedPartner) {
      updateApplicationData({ impactPartners: [selectedPartner.label] });
      setValue('impactPartners', [selectedPartner.label]);
      localStorage.setItem('orgPartnerInitialized', 'true');
    }
  }, [userSignupPartner, setValue, updateApplicationData, prioritizedPartnerOptions]);

  const submit: SubmitHandler<FormValues> = (data) => {
    if (canContinue) {
      updateApplicationData({
        impactPartners: data.impactPartners || [],
        otherPartners: data.otherPartners || '',
      });
      onContinue();
    } else {
      setShowError(true);
    }
  };

  const selectedPartners = watch('impactPartners') || [];
  const otherPartners = watch('otherPartners') || '';
  const noPartners = watch('noPartners') || false;

  const canContinue = !!otherPartners || selectedPartners.length > 0 || !!noPartners;

  const onSelectionChange = (value: string[]) => {
    setValue('impactPartners', value);
  };

  const handleBack = () => {
    updateApplicationData({
      impactPartners: watch('impactPartners'),
      otherPartners: watch('otherPartners'),
    });
    onBack();
  };

  return (
    <ApplicationBackground
      progress={progress}
      isOrganization={true}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      customLeftPanelContent={<OrgPartnerAssociationShowcase />}
      isEditProfile={props.isEditProfile}
    >
      <ApplicationPageContent
        title='Almost done! Just a few final details'
        description={
          "This helps us understand your organization's network and partners. Select from the list, add others, or check the box if none."
        }
      >
        <MultiSelectDropdown
          register={() => {
            return register('impactPartners');
          }}
          selectedItems={watch('impactPartners') || []}
          onSelectionChange={onSelectionChange}
          options={prioritizedPartnerOptions}
          label='Select any partner organizations your team is affiliated with'
          placeholder='Select as many as apply'
          error={errors.impactPartners?.message}
          filterable={true}
        />
        <InlineFormInput
          label='Are there any other partners your team is affiliated with that are not listed above?'
          placeholder='e.g. Acumen, Ashoka, Skoll Foundation...'
          register={() => register('otherPartners')}
          errors={errors.otherPartners}
        />
        <div className='flex flex-row gap-2 h-15 items-center'>
          <input type='checkbox' {...register('noPartners')} className='h-5 w-5 p-2 rounded-md' />
          <label htmlFor='noPartners'>Our organization is not affiliated with any partners</label>
        </div>
        {showError && (
          <ErrorBox showAlertTriangle={true}>
            Select at least one partner, enter a partner not listed, or confirm you have no partners
            to continue.
          </ErrorBox>
        )}
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
